import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, Plus, Minus, X, ArrowLeft, ShoppingBag, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, money, computeUnitPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import LivePreview, { ENGRAVE_FONTS, PLACEMENTS, FINISHES } from "@/components/LivePreview";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Customize() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const fileRef = useRef(null);

  const [product, setProduct] = useState(null);
  const [lines, setLines] = useState(["Your Text"]);
  const [logo, setLogo] = useState(null);
  const [font, setFont] = useState("display");
  const [placement, setPlacement] = useState("center");
  const [finish, setFinish] = useState("etched");
  const [notes, setNotes] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${productId}`).then((r) => setProduct(r.data)).catch(() => navigate("/shop"));
  }, [productId, navigate]);

  const customization = {
    text_lines: lines,
    has_logo: !!logo,
    logo,
    font,
    placement,
    finish,
    notes,
  };

  const unitPrice = product ? computeUnitPrice(product.base_price, customization) : 0;

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Please upload an image under 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(file);
  };

  const updateLine = (i, v) => setLines((p) => p.map((l, idx) => (idx === i ? v : l)));
  const addLine = () => lines.length < 4 && setLines((p) => [...p, ""]);
  const removeLine = (i) => setLines((p) => p.filter((_, idx) => idx !== i));

  const handleAdd = () => {
    const cleaned = lines.filter((l) => String(l).trim());
    if (cleaned.length === 0 && !logo) {
      toast.error("Add engraving text or a logo first");
      return;
    }
    addItem({
      product_id: product.id,
      name: product.name,
      image: product.image,
      quantity: qty,
      unit_price: unitPrice,
      customization: { ...customization, text_lines: cleaned },
    });
    toast.success(`${product.name} added to cart`);
    navigate("/cart");
  };

  if (!product) {
    return <div className="pt-40 text-center font-num tracking-widest text-muted-foreground">LOADING…</div>;
  }

  return (
    <div data-testid="customize-page" className="pt-28 pb-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Link to="/shop" className="inline-flex items-center gap-2 font-num uppercase text-xs tracking-widest text-muted-foreground hover:text-primary mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to shop
        </Link>

        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          {/* PREVIEW (sticky) */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:sticky lg:top-28"
          >
            <LivePreview image={product.image} customization={customization} shape={product.preview_shape} />
          </motion.div>

          {/* CONFIG */}
          <div>
            <span className="font-num uppercase tracking-widest text-[11px] text-primary">{product.category}</span>
            <h1 className="font-display text-5xl md:text-6xl leading-none mt-2 mb-3">{product.name}</h1>
            <p className="font-body text-muted-foreground mb-2">{product.blurb}</p>
            <p className="font-num text-2xl mb-8">{money(unitPrice)} <span className="text-sm text-muted-foreground">/ each</span></p>

            {product.firearm && (
              <div className="flex gap-3 items-start border border-primary/40 bg-primary/5 p-4 mb-8 text-sm text-foreground/80">
                <ShieldAlert className="w-5 h-5 text-primary shrink-0" />
                <span>Non-serialized parts only. We are not an FFL and do not accept firearms or serialized components.</span>
              </div>
            )}

            {/* text lines */}
            <div className="mb-8">
              <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Engraving Text</Label>
              <div className="space-y-3 mt-3">
                {lines.map((line, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      data-testid={`text-line-${i}`}
                      value={line}
                      maxLength={40}
                      onChange={(e) => updateLine(i, e.target.value)}
                      placeholder={`Line ${i + 1}`}
                      className="bg-[#111] border-white/15 rounded-none h-12 focus-visible:ring-primary"
                    />
                    {lines.length > 1 && (
                      <button onClick={() => removeLine(i)} className="px-3 border border-white/15 hover:border-primary hover:text-primary transition-colors" aria-label="remove line">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                {lines.length < 4 && (
                  <button data-testid="add-line-btn" onClick={addLine} className="flex items-center gap-2 font-num uppercase text-xs tracking-widest text-primary hover:text-white transition-colors">
                    <Plus className="w-4 h-4" /> Add line (+{money(3)})
                  </button>
                )}
              </div>
            </div>

            {/* logo upload */}
            <div className="mb-8">
              <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Logo / Artwork (+{money(8)})</Label>
              <input ref={fileRef} type="file" accept="image/*" onChange={onUpload} className="hidden" data-testid="logo-upload-input" />
              <div className="mt-3 flex items-center gap-3">
                <button
                  data-testid="logo-upload-btn"
                  onClick={() => fileRef.current?.click()}
                  className="btn-forge flex items-center gap-2 border border-white/15 px-5 py-3 font-num uppercase text-xs tracking-widest hover:border-primary hover:text-primary"
                >
                  <Upload className="w-4 h-4" /> {logo ? "Replace" : "Upload"}
                </button>
                {logo && (
                  <div className="flex items-center gap-2">
                    <img src={logo} alt="logo" className="h-10 w-10 object-contain border border-white/15 bg-[#111]" />
                    <button onClick={() => setLogo(null)} className="text-muted-foreground hover:text-primary" aria-label="remove logo"><X className="w-4 h-4" /></button>
                  </div>
                )}
              </div>
            </div>

            {/* selects */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              <div>
                <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Font</Label>
                <Select value={font} onValueChange={setFont}>
                  <SelectTrigger data-testid="font-select" className="bg-[#111] border-white/15 rounded-none h-12 mt-3"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ENGRAVE_FONTS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Placement</Label>
                <Select value={placement} onValueChange={setPlacement}>
                  <SelectTrigger data-testid="placement-select" className="bg-[#111] border-white/15 rounded-none h-12 mt-3"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLACEMENTS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Finish</Label>
                <Select value={finish} onValueChange={setFinish}>
                  <SelectTrigger data-testid="finish-select" className="bg-[#111] border-white/15 rounded-none h-12 mt-3"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FINISHES.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mb-8">
              <Label className="font-num uppercase tracking-widest text-xs text-muted-foreground">Special Notes</Label>
              <Textarea
                data-testid="notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything we should know about your design…"
                className="bg-[#111] border-white/15 rounded-none mt-3 focus-visible:ring-primary"
              />
            </div>

            {/* qty + add */}
            <div className="flex items-center gap-5 border-t border-white/10 pt-8">
              <div className="flex items-center border border-white/15">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-3 hover:text-primary" aria-label="decrease"><Minus className="w-4 h-4" /></button>
                <span data-testid="qty-value" className="w-12 text-center font-num">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="p-3 hover:text-primary" aria-label="increase"><Plus className="w-4 h-4" /></button>
              </div>
              <button
                data-testid="add-to-cart-btn"
                onClick={handleAdd}
                className="btn-forge group flex-1 flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 font-num uppercase tracking-widest text-sm hover:bg-white"
              >
                <ShoppingBag className="w-4 h-4" /> Add to cart — {money(unitPrice * qty)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
